/**
 * Investment-account subtype options surfaced in the UI.
 *
 * Values are lowercase to match Plaid's canonical account subtype strings,
 * so the same value flows through to the tax-treatment lookup server-side
 * (apps/api/src/data/tax-types.ts). Keep this list aligned with Plaid's taxonomy:
 * https://plaid.com/plaid-exchange/docs/reference/data-definitions/#account-type-and-subtype
 */
export const ACCOUNT_TAX_SUBTYPE_OPTIONS = [
  { value: 'unknown', label: 'Other / unknown' },

  // Taxable
  { value: 'brokerage', label: 'Taxable brokerage' },
  { value: 'stock plan', label: 'Employer stock plan (ESPP / RSU)' },
  { value: 'crypto exchange', label: 'Crypto exchange' },
  { value: 'non-custodial wallet', label: 'Crypto wallet (self-custody)' },
  { value: 'trust', label: 'Trust account' },
  { value: 'ugma', label: 'UGMA or UTMA' },

  // Traditional tax-deferred
  { value: 'ira', label: 'Traditional IRA' },
  { value: '401k', label: '401(k)' },
  { value: '401a', label: '401(a)' },
  { value: '403b', label: '403(b)' },
  { value: '457b', label: '457(b)' },
  { value: 'sep ira', label: 'SEP IRA' },
  { value: 'simple ira', label: 'SIMPLE IRA' },
  { value: 'thrift savings plan', label: 'Thrift Savings Plan (TSP)' },
  { value: 'profit sharing plan', label: 'Profit-sharing plan' },
  { value: 'pension', label: 'Pension' },
  { value: 'variable annuity', label: 'Variable annuity' },
  { value: 'fixed annuity', label: 'Fixed annuity' },

  // Roth
  { value: 'roth', label: 'Roth IRA' },
  { value: 'roth 401k', label: 'Roth 401(k)' },
  { value: 'roth 403b', label: 'Roth 403(b)' },
  { value: 'roth 457b', label: 'Roth 457(b)' },
  { value: 'roth thrift savings plan', label: 'Roth TSP' },

  // Triple-advantaged / qualified
  { value: 'hsa', label: 'HSA' },
  { value: '529', label: '529 plan' },
  { value: 'education savings account', label: 'Coverdell ESA' },
] as const;

const ACCOUNT_TAX_SUBTYPE_LABELS = new Map<string, string>(
  [
    ...ACCOUNT_TAX_SUBTYPE_OPTIONS.map((option) => [option.value, option.label] as const),
    ['other', 'Other / unknown'] as const,
    ['roth ira', 'Roth IRA'] as const,
    ['traditional ira', 'Traditional IRA'] as const,
    ['individual', 'Taxable brokerage'] as const,
    ['joint', 'Taxable brokerage (joint)'] as const,
    ['mutual fund', 'Mutual fund'] as const,
    ['sarsep', 'SARSEP IRA'] as const,
    ['keogh', 'Keogh plan'] as const,
    ['retirement', 'Retirement account'] as const,
    ['non-taxable brokerage account', 'Non-taxable brokerage'] as const,
    ['cash isa', 'Cash ISA'] as const,
    ['tfsa', 'TFSA'] as const,
  ],
);

export function getAccountTaxSubtypeLabel(subtype: string | null | undefined): string | null {
  if (!subtype) return null;
  const normalized = subtype.trim().toLowerCase();
  return ACCOUNT_TAX_SUBTYPE_LABELS.get(normalized) ?? subtype;
}
